/**
 * Admin — Trust & Data Safety document approval workflow.
 *
 * Lists all versions (published / draft / archived), lets admins create
 * a new DRAFT copy of the currently published version, edit its
 * `changes_from_previous` note + `next_review_date`, and approve it
 * (which archives the previous published version and publishes this one).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FilePlus2, Archive, ExternalLink, Loader2, BarChart3 } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";

const STATUS_STYLES = {
  published: { color: "#10B981", label: "Published" },
  draft: { color: "#F59E0B", label: "Draft" },
  archived: { color: "#6B7280", label: "Archived" },
};

export default function AdminTrust() {
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(null);
  const [stats, setStats] = useState(null);
  const [creating, setCreating] = useState(false);
  const [notes, setNotes] = useState("");
  const [nextReview, setNextReview] = useState("");

  const refresh = () =>
    Promise.all([
      api.get("/admin/trust/versions").then((r) => setVersions(r.data || [])).catch(() => setVersions([])),
      api.get("/admin/trust/analytics").then((r) => setStats(r.data)).catch(() => setStats(null)),
    ]);

  useEffect(() => { refresh(); }, []);

  const published = versions.find((v) => v.status === "published");

  const createDraft = async () => {
    if (!published) return toast.error("No published version to fork from.");
    setCreating(true);
    try {
      await api.post("/admin/trust/versions", {
        content: published.content,
        owner: published.owner || "Functional LAB — Data & Security",
        changes_from_previous: notes || "Routine review — no content change.",
        next_review_date: nextReview || null,
      });
      toast.success("Draft created");
      setNotes(""); setNextReview("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create draft");
    } finally { setCreating(false); }
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/trust/versions/${id}/approve`);
      toast.success("Version approved & published");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally { setBusy(null); }
  };

  const archive = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/trust/versions/${id}/archive`);
      toast.success("Version archived");
      await refresh();
    } catch (e) {
      toast.error("Archive failed");
    } finally { setBusy(null); }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="fl-label text-[#f47a20]">Admin · Trust</div>
        <h1 className="fl-heading text-4xl md:text-5xl mt-2">Trust & Data Safety — Approval</h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-2xl">
          Review, version and approve the public Trust & Data Safety statement and its
          PDF brief. Approving a draft archives the previous published version.
        </p>

        <div className="mt-8 grid md:grid-cols-3 gap-3" data-testid="trust-analytics">
          <div className="fl-card p-4">
            <div className="fl-label flex items-center gap-1"><BarChart3 size={12} /> Page visits</div>
            <div className="fl-heading text-3xl mt-1">{stats?.visits ?? "…"}</div>
          </div>
          <div className="fl-card p-4">
            <div className="fl-label">PDF downloads</div>
            <div className="fl-heading text-3xl mt-1">{stats?.downloads ?? "…"}</div>
          </div>
          <div className="fl-card p-4">
            <div className="fl-label">Published version</div>
            <div className="fl-heading text-3xl mt-1">{published?.version || "—"}</div>
          </div>
        </div>
        {stats?.note && <div className="text-[11px] text-zinc-500 mt-2">{stats.note}</div>}

        <div className="mt-8 fl-card p-5" data-testid="new-draft-card">
          <div className="fl-label text-[#f47a20] mb-3 flex items-center gap-2">
            <FilePlus2 size={14} /> Start a new review cycle
          </div>
          <label className="block text-xs text-zinc-400 mt-2">Changes from the previous version</label>
          <textarea
            className="w-full mt-1 bg-transparent border rounded-md p-2 text-sm"
            style={{ borderColor: "var(--fl-border)" }}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Updated section on wearable-data controls and clarified retention wording."
            data-testid="new-draft-notes"
          />
          <label className="block text-xs text-zinc-400 mt-3">Next review date (optional)</label>
          <input
            type="date"
            className="mt-1 bg-transparent border rounded-md p-2 text-sm"
            style={{ borderColor: "var(--fl-border)" }}
            value={nextReview}
            onChange={(e) => setNextReview(e.target.value)}
            data-testid="new-draft-next-review"
          />
          <div className="mt-3">
            <button
              onClick={createDraft}
              disabled={creating || !published}
              className="fl-btn-anim fl-btn text-sm"
              data-testid="new-draft-create"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <FilePlus2 size={14} />}
              Create draft copy
            </button>
          </div>
        </div>

        <div className="mt-8">
          <div className="fl-label text-[#f47a20] mb-3">Version history</div>
          <div className="space-y-2" data-testid="trust-versions-list">
            {versions.map((v) => {
              const s = STATUS_STYLES[v.status] || STATUS_STYLES.archived;
              return (
                <div key={v.id} className="fl-card p-4 flex flex-wrap items-center gap-3" data-testid={`trust-version-${v.version}`}>
                  <div className="min-w-[80px]">
                    <div className="fl-heading text-xl">v{v.version}</div>
                    <div className="text-[10px]" style={{ color: s.color }}>{s.label}</div>
                  </div>
                  <div className="flex-1 min-w-[240px] text-xs text-zinc-400">
                    <div className="text-zinc-300 text-sm">{v.owner || "Owner: —"}</div>
                    <div>
                      Approved: {(v.approved_at || "—").slice(0, 10)} · Last review: {(v.last_review_date || "—").slice(0, 10)} · Next review: {(v.next_review_date || "—").slice(0, 10)}
                    </div>
                    {v.changes_from_previous && (
                      <div className="mt-1 text-zinc-400 italic line-clamp-2">"{v.changes_from_previous}"</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {v.status === "draft" && (
                      <button
                        onClick={() => approve(v.id)}
                        disabled={busy === v.id}
                        className="fl-btn-anim fl-btn text-xs"
                        data-testid={`approve-${v.version}`}
                      >
                        <CheckCircle2 size={13} /> Approve & publish
                      </button>
                    )}
                    {v.status !== "archived" && (
                      <button
                        onClick={() => archive(v.id)}
                        disabled={busy === v.id}
                        className="fl-btn-anim fl-btn-ghost text-xs"
                        data-testid={`archive-${v.version}`}
                      >
                        <Archive size={13} /> Archive
                      </button>
                    )}
                    <a href="/trust" target="_blank" rel="noreferrer" className="fl-btn-anim fl-btn-ghost text-xs" data-testid={`preview-${v.version}`}>
                      <ExternalLink size={13} /> View live
                    </a>
                  </div>
                </div>
              );
            })}
            {versions.length === 0 && (
              <div className="text-sm text-zinc-500 fl-card p-4">No versions yet.</div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
